import { MjmlColumn, MjmlRaw, MjmlSection } from "@faire/mjml-react";
import {
  EmailFormat,
  Section,
  SharedProps,
  widthPixelsToPercentage,
} from "@redotech/redo-model/email-builder";
import { memo } from "react";

export type ImageState = Section.Image & SharedProps;

const createImage = (props: ImageState) => {
  const url =
    props.imageUrl ||
    "https://via.placeholder.com/600x300?text=Image+Placeholder";

  return props.format === EmailFormat.AMP ? (
    <amp-img
      alt={props.altText}
      height={1}
      layout="responsive"
      src={url}
      style={{ margin: "0 auto" }}
      width={props.aspectRatio ?? 1}
    />
  ) : (
    <img
      alt={props.altText}
      src={url}
      style={{
        width: "100%",
        height: "auto",
        display: "block",
        margin: "0 auto",
      }}
      width="100%"
    />
  );
};

const wrapWithLink = (image: React.ReactNode, props: ImageState) => {
  if (!props.clickthroughUrl || props?.isBuilder) {
    return image;
  }

  const urlWithUtm = props.utm.applyToUrl(props.clickthroughUrl);
  return (
    <a href={urlWithUtm} rel="noreferrer" target="_blank">
      {image}
    </a>
  );
};

export const EmailImage = memo(function EmailImage(props: ImageState) {
  const padding = props.sectionPadding;
  const image = createImage(props);

  const Section = ({ children }: { children: React.ReactNode }) => (
    <MjmlSection
      backgroundColor={props.sectionColor}
      paddingBottom={padding.bottom}
      paddingLeft={`${widthPixelsToPercentage(padding.left)}%`}
      paddingRight={`${widthPixelsToPercentage(padding.right)}%`}
      paddingTop={padding.top}
    >
      <MjmlColumn>
        <MjmlRaw>{children}</MjmlRaw>
      </MjmlColumn>
    </MjmlSection>
  );

  return <Section>{wrapWithLink(image, props)}</Section>;
});

export const NestedEmailImage = memo(function NestedEmailImage(
  props: ImageState,
) {
  const image = createImage(props);

  return (
    <MjmlRaw>
      <div
        style={{
          backgroundColor: props.sectionColor,
          padding: `${props.sectionPadding.top}px ${props.sectionPadding.right}px ${props.sectionPadding.bottom}px ${props.sectionPadding.left}px`,
        }}
      >
        {wrapWithLink(image, props)}
      </div>
    </MjmlRaw>
  );
});
